import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar/Sidebar';
import { toast } from 'react-toastify';
import Pagination from '../../components/Pagination/Pagination';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUsers,
  faSearch,
  faCircleInfo,
  faUserCircle
} from '@fortawesome/free-solid-svg-icons';
import { Howl } from 'howler';
import { buildApiUrl } from '../../config/api.config';

const clickDetails = new Howl({ src: ['/sounds/click.mp3'] });

const UserData = () => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage, setUsersPerPage] = useState(5);
  const navigate = useNavigate();

  const fetchUsers = async () => {
    try {
      const userDetails = JSON.parse(localStorage.getItem("userDetails"));
      const selectedRestId = localStorage.getItem("currentRestaurantId");
      let activeRestaurantId = userDetails?.role === 'owner' ? selectedRestId : userDetails?.restaurantId;

      // Handle populated restaurantId object
      if (activeRestaurantId && typeof activeRestaurantId === 'object') {
          activeRestaurantId = activeRestaurantId._id;
      }

      const response = await fetch(buildApiUrl(`users/admin/all-users?restaurantId=${activeRestaurantId}`), {
        method: 'GET',
        headers: {
          'auth-token': localStorage.getItem('token'),
        },
      });

      const data = await response.json();

      if (response.ok) {
        setUsers(data);
      } else {
        toast.error("Error fetching users");
      }
    } catch (error) {
      toast.error("An error occurred while fetching users");
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filteredUsers = users.filter((user) =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleUsersPerPageChange = (e) => {
    setUsersPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  function getInitials(name) {
    const nameArray = name.split(" ");
    const initials = nameArray.map(part => part[0]).join("");
    return initials.toUpperCase();
  }

  return (
    <div className="admin-layout">
      <Sidebar />
      <main>
        <div className="tf-card" style={{ padding: '0', overflow: 'hidden' }}>
          <header className="tf-page-header" style={{ padding: '2rem 2rem 0 2rem', marginBottom: '1.5rem' }}>
            <div className="tf-header-content">
              <div>
                <h1 className="tf-page-title">Registered Users</h1>
                <p className="tf-page-subtitle">Monitor and manage all customer accounts</p>
              </div>
              
              <div className="tf-header-actions">
                <div className="tf-search-container">
                  <FontAwesomeIcon icon={faSearch} className="tf-search-icon" />
                  <input 
                    className="tf-search-input"
                    type="text" 
                    placeholder="Search by name..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div className="tf-flex" style={{ gap: '0.8rem', background: 'var(--tf-bg-input)', padding: '0.3rem 0.8rem', borderRadius: 'var(--tf-radius-md)', border: '1px solid var(--tf-border)', height: '100%' }}>
                  <span style={{ color: 'var(--tf-text-muted)', fontSize: '0.85rem' }}>Limit:</span>
                  <select
                    className="tf-input"
                    style={{ width: 'auto', padding: '0.2rem 0.5rem', border: 'none', background: 'transparent', fontSize: '0.85rem', height: 'auto' }}
                    value={usersPerPage}
                    onChange={handleUsersPerPageChange}
                  >
                    {[5, 10, 20, 50].map(num => (
                      <option key={num} value={num}>{num}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </header>

          {currentUsers.length === 0 ? (
            <div className="tf-empty-state">
              <FontAwesomeIcon icon={faUserCircle} size="3x" style={{ marginBottom: '1.5rem', opacity: 0.3 }} />
              <p style={{ fontSize: '1.1rem' }}>{searchTerm ? 'No matching users found' : 'No user accounts recorded'}</p>
            </div>
          ) : (
            <div className="tf-list-responsive">
              <div className="tf-list-header-row" style={{ gridTemplateColumns: '1.5fr 1.5fr 100px', margin: '0 1.5rem 0.5rem 1.5rem' }}>
                <span>Identity</span>
                <span>Contact Info</span>
                <span style={{ textAlign: 'right' }}>Profile</span>
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {currentUsers.map((user) => (
                  <div key={user._id} className="tf-list-item-row" style={{ gridTemplateColumns: '1.5fr 1.5fr 100px', padding: '1.25rem 1.5rem' }}>
                    <div className="tf-flex" style={{ gap: '1rem' }}>
                      <div className="tf-avatar">
                        {getInitials(user.name)}
                      </div>
                      <div className="tf-user-info">
                        <h4>{user.name}</h4>
                        <p className="tf-text-muted" style={{ fontSize: '0.75rem', color: 'var(--tf-success)' }}>Active Customer</p>
                      </div>
                    </div>
                    <div style={{ color: 'var(--tf-text-muted)', fontSize: '0.9rem' }}>{user.email}</div>
                    <div style={{ textAlign: 'right' }}>
                      <button
                        className="tf-btn tf-btn-outline"
                        style={{ padding: '0.4rem 1rem', fontSize: '0.75rem' }}
                        onClick={() => {
                          clickDetails.play();
                          navigate(`/admin/user/dash-board/${user._id}`);
                        }}
                      >
                        Details
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              
              <div style={{ padding: '2rem' }}>
                <Pagination
                  totalItems={filteredUsers.length}
                  itemsPerPage={usersPerPage}
                  currentPage={currentPage}
                  paginate={paginate}
                />
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default UserData;